(function () {
  'use strict';

  // Shopify can evaluate an asset again when adding a section in the editor.
  if (window.BMNanoVideo) {
    window.BMNanoVideo.init(document);
    return;
  }

  var instances = new Map();
  var selector = '[data-bm-nano-video]';
  var formatTime = function (seconds) {
    var total = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
    return Math.floor(total / 60) + ':' + String(total % 60).padStart(2, '0');
  };

  function createPlayer(root) {
    var video = root.querySelector('[data-video]');
    var frame = root.querySelector('[data-video-frame]');
    var tabs = Array.from(root.querySelectorAll('[data-video-tab]'));
    var panels = Array.from(root.querySelectorAll('[data-video-panel]'));
    var track = root.querySelector('[role="tablist"]');
    var playButton = root.querySelector('[data-video-play]');
    var soundButton = root.querySelector('[data-video-sound]');
    var soundLabel = root.querySelector('[data-video-sound-label]');
    var seek = root.querySelector('[data-video-seek]');
    var time = root.querySelector('[data-video-time]');
    var fullscreenButton = root.querySelector('[data-video-fullscreen]');
    var notice = root.querySelector('[data-video-notice]');
    var status = root.querySelector('[data-video-status]');
    var retry = root.querySelector('[data-video-retry]');
    var directLink = root.querySelector('[data-video-direct-link]');
    if (!video || !frame || tabs.length !== 2 || panels.length !== 2) return null;

    var listeners = [];
    var observers = [];
    var activeIndex = 0;
    var loadedIndex = -1;
    var positions = [0, 0];
    var restoreAt = 0;
    var sourceGeneration = 0;
    var requestId = 0;
    var pendingPlayRequests = new Set();
    var nativeFullscreen = false;
    var near = false;
    // Manual controls must work even before the first observer callback.
    var inViewport = true;
    var autoplayVisible = false;
    var manualPaused = false;
    var autoplayBlocked = false;
    var wantsPlayback = false;
    var playKind = null;
    var expectedPause = false;
    var destroyed = false;
    var animationFrame = null;
    var reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
    var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    function listen(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      listeners.push(function () { target.removeEventListener(type, handler, options); });
    }

    function showNotice(message, canRetry) {
      status.textContent = message;
      notice.hidden = !message;
      retry.hidden = !canRetry;
      directLink.hidden = !canRetry;
      directLink.href = tabs[activeIndex].dataset.videoUrl;
    }

    function updateControls() {
      var playing = !video.paused && !video.ended;
      var soundOn = !video.muted && video.volume > 0;
      var duration = Number.isFinite(video.duration) ? video.duration : 0;
      var current = Number.isFinite(video.currentTime) ? video.currentTime : 0;
      var progress = duration > 0 ? Math.min(1000, Math.max(0, current / duration * 1000)) : 0;
      frame.dataset.playing = String(playing);
      playButton.setAttribute('aria-label', playing || wantsPlayback ? 'Pause video' : 'Play video');
      soundButton.setAttribute('aria-label', soundOn ? 'Turn sound off' : 'Turn sound on');
      soundButton.setAttribute('aria-pressed', String(soundOn));
      soundLabel.textContent = soundOn ? 'Sound on' : 'Sound off';
      time.textContent = formatTime(current) + ' / ' + formatTime(duration);
      seek.value = String(progress);
      seek.style.setProperty('--bm-nano-video-progress', progress / 10 + '%');
      seek.setAttribute('aria-valuetext', formatTime(current) + ' of ' + formatTime(duration));
      seek.disabled = duration <= 0;
      fullscreenButton.setAttribute('aria-label', document.fullscreenElement === frame ? 'Exit fullscreen' : 'Enter fullscreen');
    }

    function suspend() {
      wantsPlayback = false;
      playKind = null;
      requestId += 1;
      if (!video.paused) {
        expectedPause = true;
        video.pause();
      }
      updateControls();
    }

    function ensureSource(force) {
      if (loadedIndex === activeIndex && !force) return;
      sourceGeneration += 1;
      loadedIndex = activeIndex;
      restoreAt = positions[activeIndex];
      video.poster = tabs[activeIndex].dataset.posterUrl || '';
      video.src = tabs[activeIndex].dataset.videoUrl;
      video.preload = 'metadata';
      video.load();
      updateControls();
    }

    function autoplayAllowed() {
      return !reducedMotion.matches && !(connection && connection.saveData);
    }

    function isFullscreen() {
      return nativeFullscreen || video.webkitDisplayingFullscreen === true ||
        document.fullscreenElement === frame || document.fullscreenElement === video;
    }

    function playbackVisible() {
      return inViewport || isFullscreen();
    }

    function canContinue() {
      return !destroyed && wantsPlayback && !document.hidden && playbackVisible() &&
        (playKind !== 'auto' || ((autoplayVisible || isFullscreen()) && autoplayAllowed() && !manualPaused));
    }

    function requestPlay(kind) {
      if (destroyed || document.hidden || !playbackVisible()) return;
      ensureSource(false);
      wantsPlayback = true;
      playKind = kind;
      var id = ++requestId;
      var generation = sourceGeneration;
      var pendingRequest = {};
      pendingPlayRequests.add(pendingRequest);
      showNotice('', false);
      updateControls();
      var result;
      try {
        result = video.play();
      } catch (error) {
        pendingPlayRequests.delete(pendingRequest);
        handlePlayFailure(error, id, generation);
        return;
      }
      Promise.resolve(result).then(function () {
        pendingPlayRequests.delete(pendingRequest);
        // A promise from the previous source must not stop a newer valid play.
        // It must still stop playback if scrolling or a manual pause won the race.
        if (!canContinue()) {
          if (!video.paused) {
            expectedPause = true;
            video.pause();
          }
        }
        if (!destroyed) updateControls();
      }).catch(function (error) {
        pendingPlayRequests.delete(pendingRequest);
        handlePlayFailure(error, id, generation);
      });
    }

    function handlePlayFailure(error, id, generation) {
      if (destroyed || id !== requestId || generation !== sourceGeneration) return;
      wantsPlayback = false;
      playKind = null;
      // Do not repeatedly fight a browser autoplay policy on every scroll event.
      autoplayBlocked = true;
      if (error && error.name === 'AbortError') {
        updateControls();
        return;
      }
      var blocked = error && error.name === 'NotAllowedError';
      showNotice(blocked ? 'Press play to watch.' : 'This video could not load. Please try again.', !blocked);
      updateControls();
    }

    function maybeAutoplay() {
      if (!autoplayVisible || document.hidden || manualPaused || autoplayBlocked || !autoplayAllowed()) return;
      if (wantsPlayback || (!video.paused && !video.ended)) return;
      requestPlay('auto');
    }

    function togglePlay() {
      if ((!video.paused && !video.ended) || wantsPlayback) {
        manualPaused = true;
        suspend();
      } else {
        manualPaused = false;
        autoplayBlocked = false;
        requestPlay('manual');
      }
    }

    function selectTab(index, focus) {
      if (focus) tabs[index].focus();
      if (index === activeIndex) return;
      if (loadedIndex === activeIndex && Number.isFinite(video.currentTime)) positions[activeIndex] = video.currentTime;
      var continueManual = playKind === 'manual' && wantsPlayback;
      suspend();
      activeIndex = index;
      tabs.forEach(function (tab, tabIndex) {
        var selected = tabIndex === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        panels[tabIndex].hidden = !selected;
      });
      track.dataset.active = String(index);
      panels[index].appendChild(frame);
      video.setAttribute('aria-label', tabs[index].dataset.videoTitle);
      video.poster = tabs[index].dataset.posterUrl || '';
      showNotice('', false);
      if (near || inViewport) ensureSource(false);
      if (continueManual && inViewport && !document.hidden) requestPlay('manual');
      else maybeAutoplay();
      updateControls();
    }

    tabs.forEach(function (tab, index) {
      listen(tab, 'click', function () { selectTab(index, false); });
      listen(tab, 'keydown', function (event) {
        var next;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        else return;
        event.preventDefault();
        selectTab(next, true);
      });
    });

    listen(playButton, 'click', togglePlay);
    listen(video, 'click', togglePlay);
    listen(soundButton, 'click', function () {
      var turnOn = video.muted || video.volume === 0;
      if (turnOn && video.volume === 0) video.volume = 1;
      video.muted = !turnOn;
      updateControls();
    });
    listen(seek, 'input', function () {
      if (!Number.isFinite(video.duration) || video.duration <= 0) return;
      video.currentTime = video.duration * Number(seek.value) / 1000;
      positions[activeIndex] = video.currentTime;
      updateControls();
    });
    listen(retry, 'click', function () {
      manualPaused = false;
      autoplayBlocked = false;
      suspend();
      ensureSource(true);
      requestPlay('manual');
    });
    listen(fullscreenButton, 'click', function () {
      var result;
      try {
        if (document.fullscreenElement === frame && document.exitFullscreen) result = document.exitFullscreen();
        else if (frame.requestFullscreen) result = frame.requestFullscreen();
        else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
      } catch (error) { return; }
      if (result && result.catch) result.catch(function () {});
    });
    fullscreenButton.hidden = !frame.requestFullscreen && !video.webkitEnterFullscreen;
    listen(document, 'fullscreenchange', updateControls);
    listen(video, 'webkitbeginfullscreen', function () {
      nativeFullscreen = true;
    });
    listen(video, 'webkitendfullscreen', function () {
      nativeFullscreen = false;
      if (!inViewport) suspend();
      else maybeAutoplay();
    });
    listen(video, 'loadedmetadata', function () {
      if (restoreAt > 0 && Number.isFinite(video.duration)) {
        video.currentTime = Math.min(restoreAt, Math.max(0, video.duration - 0.1));
      }
      restoreAt = 0;
      updateControls();
    });
    listen(video, 'timeupdate', updateControls);
    listen(video, 'durationchange', updateControls);
    listen(video, 'volumechange', updateControls);
    listen(video, 'play', function () {
      if (!canContinue()) {
        // An idle native/OS play event is a new manual request. In contrast,
        // a play event with one of our promises still pending may be a late
        // result of a canceled request, so it cannot override a pause.
        if (!destroyed && !document.hidden && playbackVisible() && pendingPlayRequests.size === 0) {
          manualPaused = false;
          autoplayBlocked = false;
          wantsPlayback = true;
          playKind = 'manual';
          showNotice('', false);
        } else {
          suspend();
          return;
        }
      }
      updateControls();
    });
    listen(video, 'pause', function () {
      if (expectedPause) expectedPause = false;
      else if (wantsPlayback && !video.ended && !document.hidden && playbackVisible()) {
        // Respect a pause from OS media controls as well as our own button.
        manualPaused = true;
        wantsPlayback = false;
        playKind = null;
        requestId += 1;
      }
      updateControls();
    });
    listen(video, 'ended', function () {
      manualPaused = true;
      wantsPlayback = false;
      playKind = null;
      positions[activeIndex] = 0;
      updateControls();
    });
    listen(video, 'error', function () {
      suspend();
      autoplayBlocked = true;
      showNotice('This video could not load. Please try again.', true);
    });
    listen(document, 'visibilitychange', function () {
      if (document.hidden) suspend();
      else maybeAutoplay();
    });
    function preferenceChanged() {
      if (!autoplayAllowed() && playKind === 'auto') suspend();
      else maybeAutoplay();
    }
    if (reducedMotion.addEventListener) listen(reducedMotion, 'change', preferenceChanged);
    else if (reducedMotion.addListener) {
      reducedMotion.addListener(preferenceChanged);
      listeners.push(function () { reducedMotion.removeListener(preferenceChanged); });
    }
    if (connection && connection.addEventListener) listen(connection, 'change', preferenceChanged);

    function updateVisibility(ratio, intersects) {
      inViewport = intersects && ratio > 0;
      autoplayVisible = inViewport && ratio >= 0.45;
      if (autoplayVisible) near = true;
      if (!isFullscreen() && (!inViewport || (!autoplayVisible && playKind === 'auto'))) suspend();
      if (autoplayVisible) maybeAutoplay();
    }

    if ('IntersectionObserver' in window) {
      var loadingObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || destroyed) return;
          near = true;
          ensureSource(false);
          loadingObserver.disconnect();
        });
      }, { rootMargin: '300px 0px', threshold: 0 });
      var playbackObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!destroyed) updateVisibility(entry.intersectionRatio, entry.isIntersecting);
        });
      }, { threshold: [0, 0.45] });
      loadingObserver.observe(frame);
      playbackObserver.observe(frame);
      observers.push(loadingObserver, playbackObserver);
    } else {
      function measure() {
        animationFrame = null;
        var rect = frame.getBoundingClientRect();
        var height = window.innerHeight || document.documentElement.clientHeight;
        var width = window.innerWidth || document.documentElement.clientWidth;
        if (rect.bottom >= -300 && rect.top <= height + 300) {
          near = true;
          ensureSource(false);
        }
        var visibleHeight = Math.max(0, Math.min(rect.bottom, height) - Math.max(rect.top, 0));
        var visibleWidth = Math.max(0, Math.min(rect.right, width) - Math.max(rect.left, 0));
        var ratio = rect.height && rect.width ? visibleHeight * visibleWidth / (rect.height * rect.width) : 0;
        updateVisibility(ratio, ratio > 0);
      }
      function scheduleMeasure() {
        if (animationFrame === null) animationFrame = window.requestAnimationFrame(measure);
      }
      listen(window, 'scroll', scheduleMeasure, { passive: true });
      listen(window, 'resize', scheduleMeasure);
      scheduleMeasure();
    }

    video.controls = false;
    video.muted = true;
    root.dataset.enhanced = 'true';
    updateControls();

    return {
      destroy: function () {
        destroyed = true;
        suspend();
        pendingPlayRequests.clear();
        observers.forEach(function (observer) { observer.disconnect(); });
        listeners.forEach(function (remove) { remove(); });
        if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
        // Release the media request/decoder on Shopify section unload.
        video.removeAttribute('src');
        video.preload = 'none';
        video.load();
        root.removeAttribute('data-enhanced');
      }
    };
  }

  function rootsWithin(scope) {
    var roots = [];
    if (scope.matches && scope.matches(selector)) roots.push(scope);
    return roots.concat(Array.from(scope.querySelectorAll(selector)));
  }
  function init(scope) {
    rootsWithin(scope).forEach(function (root) {
      if (instances.has(root)) return;
      var instance = createPlayer(root);
      if (instance) instances.set(root, instance);
    });
  }
  function destroy(scope) {
    rootsWithin(scope).forEach(function (root) {
      var instance = instances.get(root);
      if (instance) instance.destroy();
      instances.delete(root);
    });
  }
  window.BMNanoVideo = { init: init, destroy: destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); }, { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { destroy(event.target); });
})();
